'use client';

import { c as _c } from "react/compiler-runtime";
import { type MouseEvent, type ReactElement, useId } from 'react';
import { SummarizerOptionsDialog } from '@/components/summarizer/SummarizerOptionsDialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ModelSelector } from '@/components/ui/ModelSelector';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Slider } from '@/components/ui/slider';
import { Textarea } from '@/components/ui/textarea';
import { SUMMARY_LENGTH_OPTIONS } from '@/lib/link-preview/summary-options';
import { cn } from '@/lib/utils';
type SummaryMode = 'summary' | 'explain' | 'custom';
interface TweetSummarySettingsDialogProperties {
  canonicalDraftModel: string;
  chatLabel?: string;
  chatLoadingLabel?: string;
  draftCustomPrompt: string;
  draftSummaryLengthIndex: number;
  draftSummaryMode: SummaryMode;
  draftSummarySearchEnabled: boolean;
  isChatEnabled?: boolean;
  isChatLoading?: boolean;
  isSubmitDisabled: boolean;
  isSummarizing: boolean;
  liveSearchProviderName: string | null;
  onCancel: () => void;
  onCanonicalModelChange: (canonicalId: string) => void;
  onChat?: (event: MouseEvent<HTMLButtonElement>) => void;
  onDraftCustomPromptChange: (value: string) => void;
  onDraftSummaryLengthIndexChange: (nextIndex: number) => void;
  onDraftSummaryModeChange: (mode: SummaryMode) => void;
  onDraftSummarySearchEnabledChange: (enabled: boolean) => void;
  onOpenChange: (open: boolean) => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  open: boolean;
  providerHandlesVideo: boolean;
  submitLabel: string;
  supportsSummarySearch: boolean;
}
export function TweetSummarySettingsDialog(t0) {
  const $ = _c(99);
  const {
    open,
    onOpenChange,
    isSummarizing,
    draftSummaryLengthIndex,
    onDraftSummaryLengthIndexChange,
    canonicalDraftModel,
    onCanonicalModelChange,
    draftCustomPrompt,
    onDraftCustomPromptChange,
    draftSummarySearchEnabled,
    onDraftSummarySearchEnabledChange,
    supportsSummarySearch,
    liveSearchProviderName,
    providerHandlesVideo,
    draftSummaryMode,
    onDraftSummaryModeChange,
    submitLabel,
    isSubmitDisabled,
    onSubmit,
    onCancel,
    onChat,
    isChatEnabled: t1,
    isChatLoading,
    chatLabel,
    chatLoadingLabel
  } = t0;
  const isChatEnabled = t1 === undefined ? false : t1;
  const lengthDescription = SUMMARY_LENGTH_OPTIONS[draftSummaryLengthIndex]?.description;
  const liveSearchCheckboxId = useId();
  const customPromptId = useId();
  const modeSelectionId = useId();
  const modeLabelId = useId();
  let summarySearchHelperCopy = "Web search is unavailable for the current model.";
  if (supportsSummarySearch) {
    summarySearchHelperCopy = "Enables the summarizer to fetch live context for this tweet.";
    if (liveSearchProviderName) {
      const videoSuffix = providerHandlesVideo ? " (includes video results)" : "";
      summarySearchHelperCopy = `Powered by ${liveSearchProviderName}${videoSuffix}.`;
    }
  }
  const t2 = isChatEnabled ? onChat : undefined;
  const t3 = !isChatEnabled;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Label className="font-medium">Summary length</Label>;
    $[0] = t4;
  } else {
    t4 = $[0];
  }
  let t5;
  if ($[1] !== lengthDescription) {
    t5 = <div className="flex items-center justify-between">{t4}<span className="text-xs text-muted-foreground">{lengthDescription}</span></div>;
    $[1] = lengthDescription;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] !== draftSummaryLengthIndex) {
    t6 = [draftSummaryLengthIndex];
    $[3] = draftSummaryLengthIndex;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] !== draftSummaryLengthIndex || $[6] !== onDraftSummaryLengthIndexChange) {
    t7 = value => {
      onDraftSummaryLengthIndexChange(value[0] ?? draftSummaryLengthIndex);
    };
    $[5] = draftSummaryLengthIndex;
    $[6] = onDraftSummaryLengthIndexChange;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== t6 || $[9] !== t7) {
    t8 = <Slider value={t6} onValueChange={t7} min={0} max={SUMMARY_LENGTH_OPTIONS.length - 1} step={1} />;
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== draftSummaryLengthIndex) {
    t9 = SUMMARY_LENGTH_OPTIONS.map((option, index) => <span key={option.value} className={cn(index === draftSummaryLengthIndex && "text-foreground font-medium")}>{option.label}</span>);
    $[11] = draftSummaryLengthIndex;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== t9) {
    t10 = <div className="flex justify-between text-xs text-muted-foreground">{t9}</div>;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== t10 || $[16] !== t5 || $[17] !== t8) {
    t11 = <div className="space-y-2">{t5}{t8}{t10}</div>;
    $[15] = t10;
    $[16] = t5;
    $[17] = t8;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== canonicalDraftModel || $[20] !== isSummarizing || $[21] !== onCanonicalModelChange) {
    t12 = <ModelSelector value={canonicalDraftModel} onChange={onCanonicalModelChange} isAdmin={true} showIcon={false} showLabel={true} helperText={null} labelText="Model" disabled={isSummarizing} />;
    $[19] = canonicalDraftModel;
    $[20] = isSummarizing;
    $[21] = onCanonicalModelChange;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== onDraftSummarySearchEnabledChange) {
    t13 = next => {
      onDraftSummarySearchEnabledChange(Boolean(next));
    };
    $[23] = onDraftSummarySearchEnabledChange;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  const t14 = !supportsSummarySearch;
  let t15;
  if ($[25] !== draftSummarySearchEnabled || $[26] !== liveSearchCheckboxId || $[27] !== t13 || $[28] !== t14) {
    t15 = <Checkbox id={liveSearchCheckboxId} checked={draftSummarySearchEnabled} onCheckedChange={t13} disabled={t14} />;
    $[25] = draftSummarySearchEnabled;
    $[26] = liveSearchCheckboxId;
    $[27] = t13;
    $[28] = t14;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== liveSearchCheckboxId) {
    t16 = <Label htmlFor={liveSearchCheckboxId} className="font-medium">Allow web search</Label>;
    $[30] = liveSearchCheckboxId;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== summarySearchHelperCopy) {
    t17 = <p className="text-xs text-muted-foreground">{summarySearchHelperCopy}</p>;
    $[32] = summarySearchHelperCopy;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== t16 || $[35] !== t17) {
    t18 = <div className="space-y-1">{t16}{t17}</div>;
    $[34] = t16;
    $[35] = t17;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== t15 || $[38] !== t18) {
    t19 = <div className="flex items-start gap-3">{t15}{t18}</div>;
    $[37] = t15;
    $[38] = t18;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== modeLabelId) {
    t20 = <Label id={modeLabelId} className="font-medium">Mode</Label>;
    $[40] = modeLabelId;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== onDraftSummaryModeChange) {
    t21 = value_0 => {
      if (value_0 === "summary" || value_0 === "explain" || value_0 === "custom") {
        onDraftSummaryModeChange(value_0);
      }
    };
    $[42] = onDraftSummaryModeChange;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  const t22 = `${modeSelectionId}-summary`;
  let t23;
  if ($[44] !== t22) {
    t23 = <RadioGroupItem id={t22} value="summary" />;
    $[44] = t22;
    $[45] = t23;
  } else {
    t23 = $[45];
  }
  const t24 = `${modeSelectionId}-summary`;
  let t25;
  if ($[46] !== t24) {
    t25 = <Label htmlFor={t24} className="flex-1 cursor-pointer font-medium">Summarize</Label>;
    $[46] = t24;
    $[47] = t25;
  } else {
    t25 = $[47];
  }
  let t26;
  if ($[48] !== t23 || $[49] !== t25) {
    t26 = <div className="flex items-center space-x-3 rounded-md border border-border/60 bg-background px-3 py-2 text-sm">{t23}{t25}</div>;
    $[48] = t23;
    $[49] = t25;
    $[50] = t26;
  } else {
    t26 = $[50];
  }
  const t27 = `${modeSelectionId}-explain`;
  let t28;
  if ($[51] !== t27) {
    t28 = <RadioGroupItem id={t27} value="explain" />;
    $[51] = t27;
    $[52] = t28;
  } else {
    t28 = $[52];
  }
  const t29 = `${modeSelectionId}-explain`;
  let t30;
  if ($[53] !== t29) {
    t30 = <Label htmlFor={t29} className="flex-1 cursor-pointer font-medium">Explain</Label>;
    $[53] = t29;
    $[54] = t30;
  } else {
    t30 = $[54];
  }
  let t31;
  if ($[55] !== t28 || $[56] !== t30) {
    t31 = <div className="flex items-center space-x-3 rounded-md border border-border/60 bg-background px-3 py-2 text-sm">{t28}{t30}</div>;
    $[55] = t28;
    $[56] = t30;
    $[57] = t31;
  } else {
    t31 = $[57];
  }
  const t32 = `${modeSelectionId}-custom`;
  let t33;
  if ($[58] !== t32) {
    t33 = <RadioGroupItem id={t32} value="custom" />;
    $[58] = t32;
    $[59] = t33;
  } else {
    t33 = $[59];
  }
  const t34 = `${modeSelectionId}-custom`;
  let t35;
  if ($[60] !== t34) {
    t35 = <Label htmlFor={t34} className="flex-1 cursor-pointer font-medium">Custom prompt</Label>;
    $[60] = t34;
    $[61] = t35;
  } else {
    t35 = $[61];
  }
  let t36;
  if ($[62] !== t33 || $[63] !== t35) {
    t36 = <div className="flex items-center space-x-3 rounded-md border border-border/60 bg-background px-3 py-2 text-sm">{t33}{t35}</div>;
    $[62] = t33;
    $[63] = t35;
    $[64] = t36;
  } else {
    t36 = $[64];
  }
  let t37;
  if ($[65] !== draftSummaryMode || $[66] !== modeLabelId || $[67] !== modeSelectionId || $[68] !== t21 || $[69] !== t26 || $[70] !== t31 || $[71] !== t36) {
    t37 = <RadioGroup id={modeSelectionId} value={draftSummaryMode} onValueChange={t21} className="grid gap-2 sm:grid-cols-3" aria-labelledby={modeLabelId}>{t26}{t31}{t36}</RadioGroup>;
    $[65] = draftSummaryMode;
    $[66] = modeLabelId;
    $[67] = modeSelectionId;
    $[68] = t21;
    $[69] = t26;
    $[70] = t31;
    $[71] = t36;
    $[72] = t37;
  } else {
    t37 = $[72];
  }
  let t38;
  if ($[73] !== t20 || $[74] !== t37) {
    t38 = <div className="space-y-3">{t20}{t37}</div>;
    $[73] = t20;
    $[74] = t37;
    $[75] = t38;
  } else {
    t38 = $[75];
  }
  let t39;
  if ($[76] !== customPromptId || $[77] !== draftCustomPrompt || $[78] !== draftSummaryMode || $[79] !== onDraftCustomPromptChange) {
    t39 = draftSummaryMode === "custom" ? <div className="space-y-2"><Label htmlFor={customPromptId} className="font-medium">Custom prompt</Label><Textarea id={customPromptId} value={draftCustomPrompt} onChange={event => {
        onDraftCustomPromptChange(event.target.value);
      }} placeholder="Ask a specific question or override the default instructions" className="resize-none text-sm" rows={4} maxLength={5000} /></div> : null;
    $[76] = customPromptId;
    $[77] = draftCustomPrompt;
    $[78] = draftSummaryMode;
    $[79] = onDraftCustomPromptChange;
    $[80] = t39;
  } else {
    t39 = $[80];
  }
  let t40;
  if ($[81] !== chatLabel || $[82] !== chatLoadingLabel || $[83] !== isChatLoading || $[84] !== isSubmitDisabled || $[85] !== isSummarizing || $[86] !== onCancel || $[87] !== onOpenChange || $[88] !== onSubmit || $[89] !== open || $[90] !== submitLabel || $[91] !== t11 || $[92] !== t12 || $[93] !== t19 || $[94] !== t2 || $[95] !== t3 || $[96] !== t38 || $[97] !== t39) {
    t40 = <SummarizerOptionsDialog open={open} onOpenChange={onOpenChange} isSummarizing={isSummarizing} title="Summarize tweet" description="Adjust the summary length and model before running the tweet summarizer." onSubmit={onSubmit} onCancel={onCancel} submitLabel={submitLabel} isSubmitDisabled={isSubmitDisabled} onChat={t2} isChatDisabled={t3} isChatLoading={isChatLoading} chatLabel={chatLabel} chatLoadingLabel={chatLoadingLabel}>{t11}{t12}{t19}{t38}{t39}</SummarizerOptionsDialog>;
    $[81] = chatLabel;
    $[82] = chatLoadingLabel;
    $[83] = isChatLoading;
    $[84] = isSubmitDisabled;
    $[85] = isSummarizing;
    $[86] = onCancel;
    $[87] = onOpenChange;
    $[88] = onSubmit;
    $[89] = open;
    $[90] = submitLabel;
    $[91] = t11;
    $[92] = t12;
    $[93] = t19;
    $[94] = t2;
    $[95] = t3;
    $[96] = t38;
    $[97] = t39;
    $[98] = t40;
  } else {
    t40 = $[98];
  }
  return t40;
}